const DANISH_TIME_ZONE = 'Europe/Copenhagen';

// "2027-05-18", "2027-05-18T09:30" or "2027-05-18T09:30:00.000", optionally
// followed by Z or an offset such as +02:00 or +0200
const REGEX_ISO_DATE =
  /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?(Z|[+-]\d{2}:?\d{2})?$/;

const danishDateTimeFormat = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  hour: '2-digit',
  hourCycle: 'h23',
  minute: '2-digit',
  month: '2-digit',
  second: '2-digit',
  timeZone: DANISH_TIME_ZONE,
  year: 'numeric',
});

// The date and time the moment has in Denmark, as a date in the local timezone
const toDanishWallClock = (moment: Date): Date => {
  const parts = Object.fromEntries(
    danishDateTimeFormat
      .formatToParts(moment)
      .map(part => [part.type, Number(part.value)]),
  );

  return new Date(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );
};

/**
 * Parses a date from Tourpaq (or the CMS) so that its local date and time are
 * the ones in Denmark, whatever timezone the browser is in.
 *
 * The backend sends dates as moments in Danish time, e.g.
 * "2027-05-18T00:00:00+02:00". `new Date()` would move these into the
 * browser's timezone, which makes the date Monday 23:00 for anyone behind
 * Denmark (the Canaries, Madeira, the UK...). Everything that works with the
 * result reads its local date and time (the calendars, `toDateString()` in the
 * search url, dayjs formatting), so they show and send the Danish date.
 *
 * Values without an offset ("2027-05-18", "2027-05-18T09:30:00") are kept as
 * they are. Other values, such as "Tue May 18 2027" from the search url, are
 * already a local date and are parsed as such.
 *
 * Only the result's local fields are Danish: outside Denmark it is not the
 * same moment as the input. Use its local date and time (or `toDateString()`)
 * and never send it on with `toISOString()` or `getTime()`, which would bring
 * the timezone shift back.
 *
 * @param value - The date string, or a date which is copied as it is.
 * @returns The date, with the Danish date and time as its local ones.
 */
const parseDanishDate = (value: Date | string): Date => {
  if (value instanceof Date) return new Date(value);

  const match = value.match(REGEX_ISO_DATE);

  if (!match) return new Date(value);

  const [, year, month, day, hour = 0, minute = 0, second = 0, offset] = match;

  // Out of range parts (such as 30 February) would roll over instead of failing
  const calendarDay = new Date(
    Date.UTC(Number(year), Number(month) - 1, Number(day)),
  );

  if (
    calendarDay.getUTCMonth() !== Number(month) - 1 ||
    calendarDay.getUTCDate() !== Number(day)
  ) {
    return new Date(NaN);
  }

  if (offset) {
    // With the colon (+02:00), as not every browser parses +0200
    const moment = new Date(value.replace(/([+-]\d{2})(\d{2})$/, '$1:$2'));

    return Number.isNaN(moment.getTime()) ? moment : toDanishWallClock(moment);
  }

  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
  );
};

export default parseDanishDate;
